import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { format, isToday, isTomorrow, isPast } from "date-fns";
import { fr } from "date-fns/locale";
import { motion } from "motion/react";
import {
  CalendarIcon,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Users,
  Wallet,
  ArrowRight,
  Calendar as CalendarLucide,
  ChefHat as ChefHatIcon,
  PartyPopper,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { Booking, BookingFilters } from "@/domain/booking/booking.type";
import { bookingQueries } from "@/domain/booking/booking.query";
import { BookingDetailSheet } from "@/routes/dashboard/client/-components/BookingDetailSheet";
import { OrderBy, BookingStatuses } from "@hometoq/backend/enums";
import { Image } from "@/components/ui/image";
import { getImageUrl } from "@/lib/image";

export const Route = createFileRoute("/dashboard/client/reservations")({
  component: RouteComponent,
});

type Tab = "upcoming" | "past" | "canceled" | "all";

const TAB_CONFIG: Record<
  Tab,
  { label: string; status: BookingStatuses | null; emptyTitle: string; emptyText: string }
> = {
  upcoming: {
    label: "À venir",
    status: BookingStatuses.ACCEPTED,
    emptyTitle: "Aucune réservation à venir",
    emptyText: "Trouvez un chef pour votre prochain événement.",
  },
  past: {
    label: "Passées",
    status: BookingStatuses.FINISHED,
    emptyTitle: "Vos prestations passées apparaîtront ici",
    emptyText: "Une fois votre première prestation terminée, elle s'affichera dans cet onglet.",
  },
  canceled: {
    label: "Annulées",
    status: BookingStatuses.CANCELED,
    emptyTitle: "Aucune annulation",
    emptyText: "Bonne nouvelle, aucune réservation annulée pour l'instant.",
  },
  all: {
    label: "Toutes",
    status: null,
    emptyTitle: "Aucune réservation",
    emptyText: "Vos réservations apparaîtront ici une fois créées.",
  },
};

const STATUS_BADGE: Record<
  string,
  { label: string; className: string }
> = {
  [BookingStatuses.PENDING]: {
    label: "En attente",
    className: "bg-yellow-100 text-yellow-800",
  },
  [BookingStatuses.ACCEPTED]: {
    label: "Confirmée",
    className: "bg-primary/10 text-primary",
  },
  [BookingStatuses.FINISHED]: {
    label: "Terminée",
    className: "bg-emerald-100 text-emerald-800",
  },
  [BookingStatuses.REFUSED]: {
    label: "Refusée",
    className: "bg-red-100 text-red-800",
  },
  [BookingStatuses.CANCELED]: {
    label: "Annulée",
    className: "bg-gray-100 text-gray-700",
  },
};

function RouteComponent() {
  const [tab, setTab] = useState<Tab>("upcoming");
  const [name, setName] = useState("");
  const [date, setDate] = useState<Date | undefined>();
  const [page, setPage] = useState(1);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null,
  );

  const filters: BookingFilters = {
    name,
    priceSort: null,
    dateSort: tab === "past" ? OrderBy.DESC : OrderBy.ASC,
    date: date ? format(date, "yyyy-MM-dd") : null,
    statusSort: null,
    status: TAB_CONFIG[tab].status,
    page,
  };

  const { data, isLoading } = useQuery(bookingQueries.list(filters));

  // Pour les compteurs des tabs (count par status)
  const { data: upcomingData } = useQuery(
    bookingQueries.list({
      ...filters,
      status: BookingStatuses.ACCEPTED,
      page: 1,
      name: "",
      date: null,
    }),
  );
  const { data: pastData } = useQuery(
    bookingQueries.list({
      ...filters,
      status: BookingStatuses.FINISHED,
      page: 1,
      name: "",
      date: null,
    }),
  );
  const { data: canceledData } = useQuery(
    bookingQueries.list({
      ...filters,
      status: BookingStatuses.CANCELED,
      page: 1,
      name: "",
      date: null,
    }),
  );

  const counts = {
    upcoming: upcomingData?.meta?.total ?? 0,
    past: pastData?.meta?.total ?? 0,
    canceled: canceledData?.meta?.total ?? 0,
    all:
      (upcomingData?.meta?.total ?? 0) +
      (pastData?.meta?.total ?? 0) +
      (canceledData?.meta?.total ?? 0),
  };

  const handleResetFilters = () => {
    setName("");
    setDate(undefined);
    setPage(1);
  };

  const hasActiveFilters = !!name || !!date;
  const bookings = (data?.data ?? []) as Booking[];

  return (
    <section className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      {/* ─────────  Header  ───────── */}
      <header>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary/80">
            Espace client
          </span>
          <span className="w-1 h-1 rounded-full bg-primary" />
          <span className="text-[11px] text-muted-foreground">
            Mes réservations
          </span>
        </div>
        <h1 className="font-h1 text-dark-blue">Mes réservations</h1>
        <p className="font-small text-muted-foreground mt-1.5 max-w-xl">
          Retrouvez l'ensemble de vos prestations passées, à venir et annulées.
        </p>
      </header>

      {/* ─────────  Tabs  ───────── */}
      <div className="flex flex-wrap gap-2 border-b border-border">
        {(Object.keys(TAB_CONFIG) as Tab[]).map((key) => (
          <TabButton
            key={key}
            label={TAB_CONFIG[key].label}
            count={counts[key]}
            active={tab === key}
            onClick={() => {
              setTab(key);
              setPage(1);
            }}
          />
        ))}
      </div>

      {/* ─────────  Filtres  ───────── */}
      <div className="bg-white rounded-2xl border border-border p-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 px-3 flex-1 min-w-[200px]">
          <Search size={16} className="text-muted-foreground shrink-0" />
          <Input
            placeholder="Rechercher un chef..."
            className="border-none shadow-none p-0 h-9 focus-visible:ring-0"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="h-8 w-px bg-border" />

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              type="button"
              className={cn(
                "flex items-center gap-2 px-3 h-9 font-button-small",
                !date && "text-muted-foreground",
              )}
            >
              <CalendarIcon size={16} />
              {date ? format(date, "dd/MM/yyyy") : "Filtrer par date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={date ? new Date(date) : undefined}
              onSelect={(d) => {
                setDate(d);
                setPage(1);
              }}
              locale={fr}
            />
          </PopoverContent>
        </Popover>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleResetFilters}
            className="h-9 text-muted-foreground hover:text-dark-blue"
          >
            <X size={14} className="mr-1" />
            Réinitialiser
          </Button>
        )}
      </div>

      {/* ─────────  Cards  ───────── */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-44 rounded-2xl bg-white border border-border animate-pulse"
            />
          ))}
        </div>
      ) : bookings.length === 0 ? (
        <EmptyState
          title={TAB_CONFIG[tab].emptyTitle}
          text={TAB_CONFIG[tab].emptyText}
          showCta={tab === "upcoming" || tab === "all"}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {bookings.map((booking, i) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              index={i}
              onOpen={() => setSelectedBookingId(booking.id)}
            />
          ))}
        </div>
      )}

      {/* ─────────  Pagination  ───────── */}
      {data?.meta && data.meta.lastPage > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft size={14} />
          </Button>
          <span className="font-small text-muted-foreground tabular-nums px-2">
            Page {data.meta.currentPage} / {data.meta.lastPage}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= data.meta.lastPage}
            onClick={() => setPage((p) => Math.min(data.meta.lastPage, p + 1))}
          >
            <ChevronRight size={14} />
          </Button>
        </div>
      )}

      {/* Sheet détails */}
      <BookingDetailSheet
        bookingId={selectedBookingId}
        isOpen={selectedBookingId !== null}
        onClose={() => setSelectedBookingId(null)}
      />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Tab button
// ─────────────────────────────────────────────────────────────────────────

function TabButton({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative flex items-center gap-2 px-4 py-2.5 font-button-small transition-colors -mb-px",
        active
          ? "text-primary border-b-2 border-primary"
          : "text-muted-foreground border-b-2 border-transparent hover:text-dark-blue",
      )}
    >
      {label}
      {count > 0 && (
        <span
          className={cn(
            "min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center tabular-nums",
            active
              ? "bg-primary text-white"
              : "bg-cream text-muted-foreground",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Booking card
// ─────────────────────────────────────────────────────────────────────────

function BookingCard({
  booking,
  index,
  onOpen,
}: {
  booking: Booking;
  index: number;
  onOpen: () => void;
}) {
  const date = new Date(booking.date);
  const dateLabel = isToday(date)
    ? "Aujourd'hui"
    : isTomorrow(date)
      ? "Demain"
      : format(date, "EEEE d MMMM yyyy", { locale: fr });
  const isUpcoming = !isPast(date);

  const status = STATUS_BADGE[booking.bookingStatus] ?? {
    label: booking.bookingStatus,
    className: "bg-gray-100 text-gray-700",
  };

  const chefAvatar =
    getImageUrl((booking.chef as any)?.avatar) ?? "/images/avatar_1.webp";
  const total = (booking.totalPrice ?? 0) / 100;
  const menuName = booking.menu?.name ?? "Menu sur-mesure";
  const guests = (booking as any).guests ?? 0;

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.2) }}
      className="bg-white rounded-2xl border border-border hover:border-primary/30 hover:shadow-md transition-all overflow-hidden flex flex-col"
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-3 border-b border-border">
        <div className="flex items-center gap-2 text-dark-blue min-w-0">
          <CalendarLucide size={14} className="text-primary shrink-0" />
          <p className="font-button-small capitalize truncate">{dateLabel}</p>
        </div>
        <span
          className={cn(
            "shrink-0 font-button-extra-small uppercase tracking-wider px-2.5 py-1 rounded-full",
            status.className,
          )}
        >
          {status.label}
        </span>
      </div>

      {/* Body */}
      <div className="px-5 py-4 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl overflow-hidden bg-cream shrink-0 border-2 border-white shadow-sm">
          <Image
            src={chefAvatar}
            alt={booking.chef?.firstName ?? "Chef"}
            layout="constrained"
            width={56}
            height={56}
            className="object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-button-extra-small uppercase tracking-wider text-muted-foreground">
            Avec
          </p>
          <p className="font-h4 text-dark-blue leading-tight">
            {booking.chef?.firstName ?? "Chef"}
          </p>
          <p className="font-small text-muted-foreground line-clamp-1">
            {menuName}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 grid grid-cols-2 gap-2 mb-4">
        <div className="bg-cream/40 rounded-xl px-3 py-2 flex items-center gap-2">
          <Users size={14} className="text-primary/70 shrink-0" />
          <div className="min-w-0">
            <p className="font-button-extra-small uppercase tracking-wider text-muted-foreground leading-none">
              Convives
            </p>
            <p className="font-button-small text-dark-blue tabular-nums leading-tight mt-0.5">
              {guests}
            </p>
          </div>
        </div>
        <div className="bg-cream/40 rounded-xl px-3 py-2 flex items-center gap-2">
          <Wallet size={14} className="text-primary/70 shrink-0" />
          <div className="min-w-0">
            <p className="font-button-extra-small uppercase tracking-wider text-muted-foreground leading-none">
              Total
            </p>
            <p className="font-button-small text-dark-blue tabular-nums leading-tight mt-0.5">
              {total.toFixed(2)} €
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-5 pb-4 mt-auto flex items-center gap-2">
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            "flex-1 inline-flex items-center justify-center gap-1.5 font-button-small px-4 py-2.5 rounded-xl transition-colors",
            isUpcoming
              ? "bg-dark-blue hover:bg-primary text-white"
              : "bg-cream hover:bg-cream/70 text-dark-blue border border-border",
          )}
        >
          Voir les détails
          <ArrowRight size={14} />
        </button>
      </div>
    </motion.article>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//  Empty state
// ─────────────────────────────────────────────────────────────────────────

function EmptyState({
  title,
  text,
  showCta,
}: {
  title: string;
  text: string;
  showCta: boolean;
}) {
  return (
    <div className="bg-white rounded-2xl border-2 border-dashed border-border p-10 lg:p-14 text-center">
      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
        {showCta ? (
          <ChefHatIcon className="w-6 h-6 text-primary" />
        ) : (
          <PartyPopper className="w-6 h-6 text-primary" />
        )}
      </div>
      <h3 className="font-h4 text-dark-blue">{title}</h3>
      <p className="font-small text-muted-foreground mt-1.5 max-w-md mx-auto">
        {text}
      </p>
      {showCta && (
        <Link
          to="/trouver-un-chef/chefs"
          className="inline-flex items-center gap-1.5 mt-5 bg-primary hover:bg-primary/90 text-white font-button-small px-4 py-2.5 rounded-xl transition-colors"
        >
          Trouver un chef
          <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}
